import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ExternalLink, X, Loader2 } from "lucide-react";
import { usePollExistence } from "@core/hooks/usePollExistence";

interface PollLinkProps {
  pollId: string;
  pollType?: "date" | "form" | "quizz" | "availability";
  /**
   * Slug connu de l'appelant parce qu'il vient de lire le sondage en base :
   * l'existence est alors acquise, on n'interroge pas le `localStorage`.
   *
   * Sans lui, `usePollExistence` ne consulte QUE la copie locale : un sondage
   * bien présent en base s'affichait « Sondage supprimé » dès que le
   * navigateur n'en avait plus de copie (autre appareil, cache vidé,
   * brouillon serveur). Constat de recette 2026-09-16 sur le journal.
   */
  slug?: string | null;
}

/**
 * Lien vers un sondage.
 * - `slug` fourni → lien direct (existence vérifiée côté serveur par l'appelant)
 * - sinon → vérification locale : loader, lien, ou badge « supprimé »
 */
export function PollLink({ pollId, slug }: PollLinkProps) {
  const navigate = useNavigate();
  const { t } = useTranslation("polls");
  const knownOnServer = !!slug;
  // Hook toujours appelé (règle des hooks) ; ignoré si le slug est connu.
  const { exists, poll } = usePollExistence(knownOnServer ? undefined : pollId);

  if (!knownOnServer && exists === null) {
    return (
      <span className="text-gray-400 flex items-center gap-1">
        <Loader2 className="w-3 h-3 animate-spin" />
        {t("consumption_journal.poll_link_checking")}
      </span>
    );
  }

  if (!knownOnServer && !exists) {
    return (
      <span className="text-gray-500 flex items-center gap-1">
        <X className="w-3 h-3" />
        {t("consumption_journal.poll_link_deleted")}
      </span>
    );
  }

  // Tous les types utilisent /poll/:slug/results ; à défaut de slug, l'id.
  const identifier = slug || poll?.slug || pollId;

  return (
    <button
      onClick={() => navigate(`/poll/${identifier}/results`)}
      className="text-blue-400 hover:text-blue-300 underline underline-offset-2 flex items-center gap-1 text-sm"
      data-testid="polllink-navigate"
    >
      <ExternalLink className="w-3 h-3" />
      {t("consumption_journal.poll_link_view")}
    </button>
  );
}
